import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { RefreshCw, Award } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('cap_token')}` } });

const profileDescriptions = {
  architecte: { emoji: '🏗️', color: '#00e5a0', title: 'Architecte', subtitle: 'Le bâtisseur méthodique' },
  stratege: { emoji: '🎯', color: '#4f8eff', title: 'Stratège', subtitle: "L'analyste avisé" },
  pilote: { emoji: '🚀', color: '#7c3aed', title: 'Pilote', subtitle: 'Le navigateur aguerri' }
};

const CapScorePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/profile`, hAuth());
        setProfile(res.data.profile);
        setChecklist(res.data.checklist);
      } catch (e) {
        // No profile yet
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleCheckItem = async (itemId, completed) => {
    setUpdating(true);
    try {
      const response = await axios.post(`${API}/profile/cap-score`, { item_id: itemId, completed }, hAuth());
      setProfile(prev => ({ ...prev, cap_score: response.data.cap_score, cap_checklist: response.data.cap_checklist }));
      if (completed) toast.success('Objectif validé ! +XP');
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><RefreshCw className="w-8 h-8 text-cap-emerald animate-spin" /></div>;

  const profileInfo = profile ? profileDescriptions[profile.profile_type] : null;
  const capScore = profile?.cap_score || 0;
  const capChecklist = profile?.cap_checklist || {};

  const getCategoryProgress = (categoryKey) => {
    if (!checklist?.[categoryKey]) return { completed: 0, total: 0, points: 0, maxPoints: 0 };
    const category = checklist[categoryKey];
    const items = Object.entries(category.items);
    const completedItems = items.filter(([id]) => capChecklist[id]);
    const points = completedItems.reduce((sum, [, item]) => sum + item.points, 0);
    return { completed: completedItems.length, total: items.length, points, maxPoints: category.points };
  };

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto space-y-6" data-testid="cap-score-page">
      <h1 className="font-display text-3xl font-bold text-white mb-1">CAP Score</h1>
      <p className="text-cap-text-secondary text-sm">Suivez votre progression, votre profil investisseur et débloquez des points en validant vos objectifs.</p>

      {profile && profileInfo && (
        <>
          {/* ═══ HERO — Profil + Niveau + XP + Score ═══ */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#1a1d27] border border-white/5 rounded-xl p-6 lg:p-8">
            <div className="flex flex-col lg:flex-row items-center gap-6">
              <div className="w-20 h-20 rounded-full flex items-center justify-center text-4xl" style={{ backgroundColor: `${profileInfo.color}20`, border: `2px solid ${profileInfo.color}` }}>
                {profileInfo.emoji}
              </div>
              <div className="flex-1 text-center lg:text-left">
                <h2 className="font-display text-2xl font-bold" style={{ color: profileInfo.color }} data-testid="investor-title">{profileInfo.title}</h2>
                <p className="text-cap-text-secondary">{profileInfo.subtitle}</p>
                <div className="flex items-center justify-center lg:justify-start gap-3 mt-2 text-sm flex-wrap">
                  <span className="px-3 py-1 rounded-full bg-[#0d0f14] text-cap-text-secondary">{user?.username}</span>
                  <span className="px-3 py-1 rounded-full bg-[#00e5a0]/10 text-[#00e5a0]" data-testid="user-level">Niveau {user?.level || 1}</span>
                  <span className="text-cap-text-muted" data-testid="user-xp">{user?.xp || 0} XP</span>
                </div>
              </div>
              <div className="text-center">
                <div className="relative w-28 h-28 rounded-full flex items-center justify-center" style={{ background: `conic-gradient(${profileInfo.color} ${capScore}%, transparent ${capScore}%)`, padding: '3px' }}>
                  <div className="w-full h-full rounded-full bg-[#1a1d27] flex items-center justify-center">
                    <div><span className="font-display text-3xl font-bold text-white" data-testid="cap-score-value">{capScore}</span><span className="text-cap-text-muted text-xs block">/ 100</span></div>
                  </div>
                </div>
                <p className="text-cap-text-secondary mt-2 text-xs">CAP Score</p>
              </div>
            </div>
            <Button onClick={() => navigate('/questionnaire')} variant="ghost" className="mt-4 text-cap-text-secondary hover:text-white text-xs" data-testid="retake-questionnaire-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refaire le questionnaire
            </Button>
          </motion.div>

          {/* ═══ Catégories à cocher ═══ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {checklist && Object.entries(checklist).map(([categoryKey, category], index) => {
              const progress = getCategoryProgress(categoryKey);
              return (
                <motion.div key={categoryKey} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 + index * 0.06 }} className="bg-[#1a1d27] border border-white/5 rounded-xl p-6" data-testid={`category-${categoryKey}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-sm font-semibold text-white">{category.name}</h3>
                    <span className="text-xs text-cap-text-secondary">{progress.points}/{progress.maxPoints} pts</span>
                  </div>
                  <Progress value={(progress.points / progress.maxPoints) * 100} className="h-2 mb-4 bg-[#0d0f14]" />
                  <div className="space-y-2">
                    {Object.entries(category.items).map(([itemId, item]) => {
                      const done = capChecklist[itemId];
                      return (
                        <div key={itemId} className={`flex items-center gap-3 p-2.5 rounded-lg transition-all cursor-pointer ${done ? 'bg-[#00e5a0]/10 border border-[#00e5a0]/20' : 'bg-[#0d0f14] hover:bg-[#141720]'}`} onClick={() => handleCheckItem(itemId, !done)} data-testid={`checklist-item-${itemId}`}>
                          <Checkbox checked={done} onCheckedChange={c => handleCheckItem(itemId, c)} disabled={updating} className="border-white/20 data-[state=checked]:bg-[#00e5a0] data-[state=checked]:border-[#00e5a0]" />
                          <span className={`flex-1 text-xs ${done ? 'text-white' : 'text-cap-text-secondary'}`}>{item.label}</span>
                          <span className={`text-[10px] font-medium ${done ? 'text-[#00e5a0]' : 'text-cap-text-muted'}`}>+{item.points}</span>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </>
      )}

      {!profile && (
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="bg-[#1a1d27] border border-white/5 rounded-xl p-6">
          <div className="text-center py-4">
            <Award className="w-10 h-10 text-cap-text-muted mx-auto mb-3" />
            <p className="text-cap-text-secondary text-sm mb-4">Répondez au questionnaire pour découvrir votre profil et votre CAP Score.</p>
            <Button onClick={() => navigate('/questionnaire')} className="bg-[#00e5a0] text-[#0d0f14] font-semibold hover:bg-[#00e5a0]/90" data-testid="start-questionnaire-btn">
              Commencer le questionnaire
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default CapScorePage;
